import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IdentityModal } from '../app/IdentityModal';
import { isSessionLockPublicPath } from '../navigation/sessionLockPublicPaths';
import { useIdentity } from './useIdentity';

type IdentityModalContextValue = {
  openIdentityModal: () => void;
  closeIdentityModal: () => void;
  /** Hide the unlock dialog without unlocking. No-op unless the session is locked. */
  minimizeSessionLock: () => void;
};

const IdentityModalContext = createContext<IdentityModalContextValue | null>(null);

export function IdentityModalProvider({ children }: { children: ReactNode }) {
  const { status: identityStatus, identity, suspensionInfo } = useIdentity();
  const location = useLocation();
  const navigate = useNavigate();
  const isIdentitySuspended = identityStatus === 'suspended' && !!suspensionInfo;
  const isIdentityLoggedIn = identityStatus === 'logged_in' && identity;
  const isIdentityLocked = identityStatus === 'locked' && !!identity;

  const [isOpen, setIsOpen] = useState(false);
  const [lockMinimized, setLockMinimized] = useState(false);
  /** Set while minimize is navigating off a private route, so that route does not immediately reopen the dialog. */
  const pendingPublicNav = useRef(false);

  useEffect(() => {
    if (!isIdentityLocked) {
      setLockMinimized(false);
      pendingPublicNav.current = false;
      return;
    }
    setIsOpen(true);
    setLockMinimized(false);
  }, [isIdentityLocked]);

  useEffect(() => {
    if (!isIdentityLocked || !lockMinimized) return;
    if (isSessionLockPublicPath(location.pathname)) {
      pendingPublicNav.current = false;
      return;
    }
    if (pendingPublicNav.current) return;
    setLockMinimized(false);
    setIsOpen(true);
  }, [isIdentityLocked, lockMinimized, location.pathname]);

  const openIdentityModal = useCallback(() => {
    pendingPublicNav.current = false;
    setLockMinimized(false);
    setIsOpen(true);
  }, []);

  const closeIdentityModal = useCallback(() => {
    if (isIdentityLocked) return;
    setIsOpen(false);
  }, [isIdentityLocked]);

  const minimizeSessionLock = useCallback(() => {
    if (!isIdentityLocked) return;
    if (!isSessionLockPublicPath(location.pathname)) {
      pendingPublicNav.current = true;
      navigate('/about/learn');
    }
    setLockMinimized(true);
    setIsOpen(false);
  }, [isIdentityLocked, location.pathname, navigate]);

  const value = useMemo(
    () => ({ openIdentityModal, closeIdentityModal, minimizeSessionLock }),
    [closeIdentityModal, minimizeSessionLock, openIdentityModal],
  );

  const showModalHost =
    !isIdentitySuspended && (!isIdentityLoggedIn || isIdentityLocked);

  const modalOpen = isIdentityLocked ? isOpen && !lockMinimized : isOpen;

  return (
    <IdentityModalContext.Provider value={value}>
      {children}
      {showModalHost ? (
        <IdentityModal
          isOpen={modalOpen}
          onClose={closeIdentityModal}
          unlockMode={!!isIdentityLocked}
          onMinimize={isIdentityLocked ? minimizeSessionLock : undefined}
        />
      ) : null}
    </IdentityModalContext.Provider>
  );
}

export function useIdentityModal(): IdentityModalContextValue {
  const ctx = useContext(IdentityModalContext);
  if (!ctx) {
    throw new Error('useIdentityModal must be used within IdentityModalProvider');
  }
  return ctx;
}

/** Returns null when rendered outside IdentityModalProvider (e.g. unauthenticated public shell). */
export function useOptionalIdentityModal(): IdentityModalContextValue | null {
  return useContext(IdentityModalContext);
}
